import { ViewEncapsulation } from '@angular/core';
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { FormlyFormOptions } from '@ngx-formly/core';
import { ActivatedRoute, Router } from '@angular/router';
import { switchMap, map, catchError } from 'rxjs/operators';
import { FormService, ISubmitResponse } from './form.service';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { cloneDeep, get, isEmpty } from 'lodash';
import {
  IFormTabRendering,
  prepareFormForRendering,
} from './utils/prepare-for-rendering';
import { createFormGroupConfigFromRenderingTabs } from './utils/create-form-group-config-from-rendering-tabs';
import { DomSanitizer, SafeUrl, Title } from '@angular/platform-browser';
import { IPublicForm, Position } from './model/form.model';
import { IField } from './model/field';
import {
  AcknowledgmentMessage,
  AcknowledgmentTitle,
  defaultLang,
} from '../shared/constants';
import { v4 as uuid } from 'uuid';

@Component({
  selector: 'app-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormComponent implements OnInit, OnDestroy {
  model = {};
  tabs: IFormTabRendering[];
  form: IPublicForm;
  options: FormlyFormOptions[][];
  subscription = new Subscription();
  submitInProgress = false;
  formGroup = new UntypedFormGroup({});
  typeFields: IField[];
  logoBase64: SafeUrl;
  logoPosition = Position;
  objectChooserField = [];

  selectedIndex = 0;

  constructor(
    protected route: ActivatedRoute,
    protected formService: FormService,
    protected snackBar: MatSnackBar,
    protected cdr: ChangeDetectorRef,
    protected fb: UntypedFormBuilder,
    protected sanitizer: DomSanitizer,
    protected router: Router,
    protected titleService: Title
  ) {}

  moveToNextTab(): void {
    this.selectedIndex = this.selectedIndex + 1;
  }

  ngOnInit() {
    this.initForm();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  submitForm() {
    if (this.formGroup.valid) {
      let submitModel = this.formGroup.value;
      let submitter$: Observable<ISubmitResponse>;
      submitter$ = this.formService.submitForm$(
        this.form.tip,
        submitModel,
        this.typeFields
      );
      this.snackBar.open('Submitting form');
      this.submitInProgress = true;
      this.cdr.detectChanges();

      this.subscription.add(
        submitter$
          .pipe(
            catchError((error) => {
              this.submitInProgress = false;
              this.cdr.detectChanges();
              this.handleSubmitError(null, error);
              return of(null);
            }),
            map((response: ISubmitResponse) => {
              this.submitInProgress = false;
              this.cdr.detectChanges();
              if (response) {
                if (response.success) {
                  this.snackBar.dismiss();
                  this.formService.acknowledgmentScreenInfo.next({
                    title: AcknowledgmentTitle,
                    message: AcknowledgmentMessage,
                  });
                  this.router.navigate(['acknowledgment-screen']);
                } else {
                  this.handleSubmitError(response);
                }
              }
            })
          )
          .subscribe(() => {})
      );
    } else {
      console.log('the form has invalid fields');
    }
  }

  handleSubmitError(response?: ISubmitResponse, error?: Error) {
    if (response) {
      console.log('[FormComponent/handleSubmitError] Response', { response });
    }
    if (error) {
      console.log('[FormComponent/handleSubmitError] Thrown error', { error });
    }
    const snackBarRef = this.snackBar.open(
      'Could not submit form',
      'Try again'
    );
    this.subscription.add(
      snackBarRef.onAction().subscribe(() => snackBarRef.dismiss())
    );
  }

  clearForm() {
    this.model = {};
    this.cdr.detectChanges();
  }

  initForm() {
    this.subscription.add(
      combineLatest([this.route.parent.url, this.route.params])
        .pipe(
          switchMap(([url, params]: [any, any]) => {
            return this.formService.getForm$(params.formTip);
          })
        )
        .subscribe((form: IPublicForm) => {
          if (form) {
            this.loadForm(form);
          } else {
            const snackBarRef = this.snackBar.open('Unable to load form.');
            this.router.navigate(['/page-not-found']);
            this.subscription.add(
              snackBarRef.onAction().subscribe(() => snackBarRef.dismiss())
            );
          }
        })
    );
  }

  loadForm(form: IPublicForm) {
    this.form = form;
    this.titleService.setTitle(get(form, 'name', 'Noggin Public Forms'));
    if (this.form.logo) {
      this.logoBase64 = 'data:image/svg+xml;base64,' + this.form.logo;
    }
    this.buildFormlyOptions();
    this.createTypeFields(form);
    this.render();
    this.sortFieldsWithType();
    this.cdr.detectChanges();
  }

  createTypeFields(form: IPublicForm) {
    const fields: IField[] = [];
    form.tabs.forEach((tab) => {
      const userLang = navigator.language.toString().toLocaleLowerCase();
      tab.tabName = get(
        tab,
        `i18n_tabName.${userLang}`,
        get(tab, `i18n_tabName.${defaultLang}`, get(tab, 'tabName'))
      );
      tab.rows.forEach((row) => {
        row.fields.forEach((field) => {
          const fieldTip = get(field, 'templateOptions.contextTypeTip');
          if (isEmpty(fieldTip)) {
            field.templateOptions.contextTypeTip = uuid();
          }
          if (
            isEmpty(get(field, 'templateOptions.appearance')) &&
            get(field, 'type') !== 'text-content' &&
            get(field, 'type') !== 'section-header' &&
            get(field, 'type') !== 'checkbox'
          ) {
            field.templateOptions.appearance = 'outline';
          }
          const fieldMap: IField = {
            label: get(
              field,
              `templateOptions.i18n_label.${userLang}`,
              get(field, `templateOptions.i18n_label.${defaultLang}`, '')
            ),
            $tip: get(field, 'templateOptions.contextTypeTip'),
            isDateOnly: get(field, 'templateOptions.type', null) === 'date', // we use this later on... before submitting the data
          };

          fields.push(fieldMap);
        });
      });
    });
    this.typeFields = fields;
  }

  buildFormlyOptions() {
    this.options = this.form.tabs.map((tab) =>
      tab.rows.map(() => ({} as FormlyFormOptions))
    );
  }

  render() {
    if (this.form) {
      let tabs: IFormTabRendering[];
      const formTabs = cloneDeep(this.form.tabs);

      const typeFieldsWithObjectSchemaProperties =
        this.formService.mergeObjectSchemaWithFormFields(
          formTabs,
          this.typeFields
        );

      tabs = prepareFormForRendering({
        tabs: formTabs,
        typeFields: typeFieldsWithObjectSchemaProperties,
      });

      const config = createFormGroupConfigFromRenderingTabs(tabs);
      this.formGroup = this.fb.group(config);

      this.tabs = tabs;
    }
  }

  /*
   * sortFieldsWithType() is to find only object chooser field and push it to an array.
   */
  sortFieldsWithType(): void {
    this.form.tabs.map((tab) => {
      tab.rows.map((row) => {
        row.fields.filter((field) => {
          if (field.type === 'object-chooser') {
            this.objectChooserField.push(field.templateOptions.contextTypeTip);
          }
        });
      });
    });
  }
}
